import { PaymentSnapshotBalances } from '@willow/types-iso';

import { add } from '../utils/math';

export interface WriteoffAmounts {
  escrow: number;
  suspense: number;
  principal: number;
}

interface Problem {
  message: string;
  account: 'escrow' | 'suspense' | 'principal';
}

type Balances = Pick<
  PaymentSnapshotBalances,
  'escrowBalance' | 'statementSuspenseBalance' | 'futureStatementSuspenseBalance'
> & { outstandingBalance?: number };

/**
 * Validates the amounts of a writeoff.
 *
 * @param currentBalances The current balances of the loan.
 * @param toWriteoff The amounts to write off.
 * @returns A list of problems with the writeoff. If empty, the writeoff is valid.
 */
export function validateWriteoffAmounts(currentBalances: Balances, toWriteoff: WriteoffAmounts): Problem[] {
  const problems: Problem[] = [];

  if (toWriteoff.escrow < 0) {
    problems.push({
      message: 'Write off amount cannot be negative',
      account: 'escrow',
    });
  }

  if (toWriteoff.suspense < 0) {
    problems.push({
      message: 'Write off amount cannot be negative',
      account: 'suspense',
    });
  }

  if (toWriteoff.principal < 0) {
    problems.push({
      message: 'Write off amount cannot be negative',
      account: 'principal',
    });
  }

  if (toWriteoff.escrow === 0 && toWriteoff.suspense === 0 && toWriteoff.principal === 0) {
    problems.push({
      message: 'Total amount must be greater than 0',
      account: 'escrow',
    });
    problems.push({
      message: 'Total amount must be greater than 0',
      account: 'suspense',
    });
    problems.push({
      message: 'Total amount must be greater than 0',
      account: 'principal',
    });
  }

  if (toWriteoff.escrow > 0) {
    if (currentBalances.escrowBalance >= 0) {
      problems.push({
        message: 'Cannot write off non-negative escrow balance',
        account: 'escrow',
      });
    } else if (add(toWriteoff.escrow, currentBalances.escrowBalance) > 0) {
      problems.push({
        message: 'Cannot write off more than the escrow negative balance',
        account: 'escrow',
      });
    }
  }

  if (toWriteoff.suspense > 0) {
    // TODO: enhance this to allow us to write off other suspense balances
    const statementSuspenseTotal = currentBalances.statementSuspenseBalance;
    if (statementSuspenseTotal >= 0) {
      problems.push({
        message: 'Cannot write off non-negative suspense balance',
        account: 'suspense',
      });
    } else if (add(toWriteoff.suspense, statementSuspenseTotal) > 0) {
      problems.push({
        message: 'Cannot write off more than the suspense negative balance',
        account: 'suspense',
      });
    }
  }

  if (toWriteoff.principal > 0) {
    const outstandingBalance = currentBalances.outstandingBalance ?? 0;
    if (outstandingBalance <= 0) {
      problems.push({
        message: 'Cannot write off non-positive principal balance',
        account: 'principal',
      });
    } else if (toWriteoff.principal > outstandingBalance) {
      problems.push({
        message: 'Cannot write off more than the outstanding principal balance',
        account: 'principal',
      });
    }
  }

  return problems;
}
